/**
 * /intro 소개 페이지 (D108 · 히어로챗 /intro 판). 로그인 없이 한 쪽에서 서비스를 알 수 있게 합니다.
 * 쓰는 곳: PG사 심사 · 앱마켓 등록의 마케팅 주소 · 공유의 첫 화면. 약관은 /legal/terms · /legal/privacy 로 따로 건넵니다(바닥 줄 없음).
 *
 * **글은 새로 쓰지 않고 이미 있는 것만 씁니다**(사용자 지정 2026-10-16): 부제 · 공유 미리보기(index.html og) 문구 ·
 * 멤버 원본(config/members) · 가상 인물 고지(약관 화면과 같은 줄) · 만 19세(이용약관). 요금 칸은 결제가 붙은 뒤에 더합니다.
 * 멤버 카드 줄은 천천히 쉬지 않고 흐르고(히어로챗과 같음), 누르거나 올리면 멈추며, 움직임 줄이기를 켜면 흐르지 않습니다.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MEMBER_KEYS, MEMBERS } from '@config/members/index.js';
import { Avatar } from '../../components/Avatar.jsx';
import { WordmarkStack } from '../../components/Brand.jsx';
import { SceneBackdrop } from '../welcome/SceneCards.jsx';

// 공유 미리보기(client/index.html og:title · og:description)와 같은 글
const HERO_TITLE = '따스한 사람들이 모인 골목 끝 가게';
const HERO_LEAD = '차 한 잔 하며, 소소한 인생 얘기 나누고 가요.';
// 약관 · AI 고지 화면(LegalIndexPage)과 같은 고지
const AI_NOTICE = '인생상회의 멤버들은 AI가 연기하는 가상 인물이에요. 운세는 재미로 봐 주세요.';
const NOTES = [AI_NOTICE, '만 19세 이상만 가입할 수 있어요.'];

const ROLL_PX_PER_SEC = 40;
const RESUME_MS = 1000;

function MemberRail() {
    const rail = useRef(null);
    const pausedUntil = useRef(0);
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    useEffect(() => {
        const element = rail.current;
        if (!element || reduced) {
            return undefined;
        }
        let frame = 0;
        let last = 0;
        let position = element.scrollLeft;
        const step = (time) => {
            const elapsed = last ? Math.min(time - last, 64) : 0;
            last = time;
            if (Date.now() < pausedUntil.current || document.hidden) {
                position = element.scrollLeft;
            } else {
                // 두 벌을 그려 두고 앞의 한 벌을 다 지나면 그만큼 되감습니다(되감는 순간이 보이지 않음)
                const second = element.children[MEMBER_KEYS.length];
                const loop = second ? second.offsetLeft - element.children[0].offsetLeft : element.scrollWidth / 2;
                position += (ROLL_PX_PER_SEC * elapsed) / 1000;
                if (position >= loop) {
                    position -= loop;
                }
                element.scrollLeft = position;
            }
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [reduced]);

    const hold = () => {
        pausedUntil.current = Number.POSITIVE_INFINITY;
    };
    const release = () => {
        pausedUntil.current = Date.now() + RESUME_MS;
    };

    const card = (memberKey, copy) => {
        const member = MEMBERS[memberKey];
        return (
            <li
                key={`${copy ? 'b' : 'a'}-${memberKey}`}
                aria-hidden={copy || undefined}
                className="flex w-[240px] shrink-0 flex-col gap-2 rounded-card border border-hairline bg-surface p-4"
            >
                <div className="flex items-center gap-3">
                    <Avatar memberKey={memberKey} size={48} />
                    <div className="flex min-w-0 flex-col">
                        <b className="text-[16px] leading-6 font-semibold text-text-primary">{member.name}</b>
                        <span className="truncate text-[12px] leading-[18px] text-text-secondary">{member.short}</span>
                    </div>
                </div>
                <p className="m-0 text-[14px] leading-[22px] text-text-primary">{member.line}</p>
                {member.quote ? (
                    <p className="m-0 font-serif text-[14px] leading-[22px] text-text-secondary">{member.quote}</p>
                ) : null}
            </li>
        );
    };

    return (
        <ul
            ref={rail}
            onPointerEnter={hold}
            onPointerLeave={release}
            onTouchStart={hold}
            onTouchEnd={release}
            className={`m-0 flex list-none gap-3 overflow-x-auto p-0 pb-1 [scrollbar-width:none] ${reduced ? 'snap-x' : ''}`}
        >
            {MEMBER_KEYS.map((memberKey) => card(memberKey, false))}
            {reduced ? null : MEMBER_KEYS.map((memberKey) => card(memberKey, true))}
        </ul>
    );
}

export function IntroPage() {
    const navigate = useNavigate();
    // 로그인했으면 별채대화, 아니면 문지기가 시작 화면(L0b)으로 보냅니다
    const start = () => navigate('/');

    return (
        <div className="min-h-dvh bg-bg">
            <SceneBackdrop image="alley" top={200} topOpacity={0.55} bottom={520} bottomOpacity={0.92} bottomFrom={42}>
                <header className="flex min-h-[86dvh] flex-1 flex-col items-center justify-end px-5 pb-10 text-center">
                    <WordmarkStack
                        textSize={40}
                        lineHeight={48}
                        markSize={52}
                        tone="cream"
                        subtitle="차 한 잔 내어 줄게, 쉬었다 가"
                    />
                    <h1 className="m-0 mt-8 text-[20px] leading-[30px] font-bold text-bg">{HERO_TITLE}</h1>
                    <p className="m-0 mt-2 text-[15px] leading-6 text-[#E9E1D4]">{HERO_LEAD}</p>
                    <button
                        type="button"
                        onClick={start}
                        className="mt-8 h-[52px] w-full max-w-[360px] rounded-card bg-bg text-[16px] font-bold text-brand"
                    >
                        시작하기
                    </button>
                    <p className="m-0 mt-4 max-w-[320px] text-[12px] leading-[18px] text-[#D8CFC2]">{AI_NOTICE}</p>
                </header>
            </SceneBackdrop>

            <section className="px-5 pt-10 pb-8">
                <h2 className="m-0 mb-4 text-[18px] leading-[26px] font-bold text-text-primary">
                    별채에 모이는 다섯 사람
                </h2>
                <MemberRail />
            </section>

            <section className="px-5 pt-2 pb-[calc(48px+env(safe-area-inset-bottom,0px))]">
                <h2 className="m-0 mb-3 text-[18px] leading-[26px] font-bold text-text-primary">알아 두실 것</h2>
                <ul className="m-0 flex list-disc flex-col gap-2 pl-5 text-[14px] leading-[22px] text-text-secondary">
                    {NOTES.map((note) => (
                        <li key={note}>{note}</li>
                    ))}
                </ul>
            </section>
        </div>
    );
}
